import React, { useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { faPause, faPlay } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface StoryContainerProps {
  slides: any[];
  onComplete: () => void;
  renderSlide: (slide: any) => React.ReactNode;
  duration?: number; // duration per slide in ms
  backgroundImage?: string; // Add background image prop
}

export function StoryContainer({ slides, onComplete, renderSlide, duration = 5000, backgroundImage }: StoryContainerProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const progressRef = useRef<number>(0);
  const lastUpdateTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    let animationFrameId: number;

    const update = () => {
      if (!isPaused) {
        const now = Date.now();
        const deltaTime = now - lastUpdateTimeRef.current;
        lastUpdateTimeRef.current = now;

        progressRef.current += (deltaTime / duration) * 100;

        if (progressRef.current >= 100) {
          if (currentIndex < slides.length - 1) {
            setCurrentIndex(prev => prev + 1);
            progressRef.current = 0;
            setProgress(0);
          } else {
            onComplete();
            return;
          }
        }
        setProgress(progressRef.current);
      } else {
        lastUpdateTimeRef.current = Date.now();
      }
      animationFrameId = requestAnimationFrame(update);
    };

    animationFrameId = requestAnimationFrame(update);
    return () => cancelAnimationFrame(animationFrameId);
  }, [currentIndex, isPaused, duration, slides.length, onComplete]);

  const handleNext = () => {
    if (currentIndex < slides.length - 1) {
      setCurrentIndex(prev => prev + 1);
      progressRef.current = 0;
      setProgress(0);
      lastUpdateTimeRef.current = Date.now();
    } else {
      onComplete();
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      progressRef.current = 0;
      setProgress(0);
      lastUpdateTimeRef.current = Date.now();
    }
  };

  const handlePointerDown = () => setIsPaused(true);
  const handlePointerUp = () => setIsPaused(false);

  const togglePause = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsPaused(!isPaused);
    lastUpdateTimeRef.current = Date.now();
  };

  const isCompletionSlide = slides[currentIndex]?.type === 'completion';

  return (
    <div className="flex-1 flex flex-col relative w-full h-full overflow-hidden select-none">
      {/* Static Background */}
      {backgroundImage && (
        <div 
          className="absolute inset-0 z-0 bg-cover bg-center transition-opacity duration-700"
          style={{ 
            backgroundImage: `url(${backgroundImage})`,
            backgroundRepeat: 'no-repeat',
          }}
        />
      )}

      {/* Progress Bars */}
      <div className="absolute top-3 left-0 right-0 z-[60] flex gap-1 px-4">
        {slides.map((_, idx) => (
          <div key={idx} className="h-0.5 flex-1 bg-white/20 rounded-full overflow-hidden backdrop-blur-sm">
            <div 
              className={clsx(
                "h-full bg-white transition-all duration-100 ease-linear",
                idx < currentIndex ? "w-full" : idx === currentIndex ? "" : "w-0"
              )}
              style={idx === currentIndex ? { width: `${progress}%` } : {}}
            />
          </div>
        ))}
      </div>

      {/* Slide Content */}
      <div className="flex-1 flex flex-col relative z-10 min-h-0 overflow-hidden">
        <div className="flex-1 overflow-y-auto custom-scrollbar pt-4 px-4 animate-in fade-in duration-500">
          <div className="min-h-full flex flex-col justify-center w-full">
            {renderSlide(slides[currentIndex])}
          </div>
        </div>
      </div>

      {/* Navigation Areas - Only for click navigation, not blocking scroll */}
      {!isCompletionSlide && (
        <div className="absolute inset-0 z-40 flex pointer-events-none">
          <div 
            className="flex-1 cursor-pointer pointer-events-auto" 
            onClick={handlePrev}
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
          />
          <div 
            className="flex-1 cursor-pointer pointer-events-auto" 
            onClick={handleNext}
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
          />
        </div>
      )}

      {/* Pause/Play Button (Bottom Left) */}
      {!isCompletionSlide && (
        <div className="absolute bottom-6 left-6 z-50 pointer-events-auto">
          <button 
            onClick={togglePause}
            className="w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center text-black shadow-lg active:scale-90 transition-all hover:scale-110 backdrop-blur-sm"
          >
            <FontAwesomeIcon icon={isPaused ? faPlay : faPause} className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <style jsx global>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.3);
        }
      `}</style>
    </div>
  );
}
